import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { useCoinStore } from '../../stores/coinStore';

const MAX_LENGTH = 500;
const COST_PER_MESSAGE = 2;

interface ChatInputProps {
  onSend: (content: string) => void;
  isGenerating: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSend, isGenerating }) => {
  const { t } = useTranslation('creative');
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const balance = useCoinStore((s) => s.account?.balance ?? 0);

  const charCount = input.length;
  const isOverLimit = charCount > MAX_LENGTH;
  const insufficientCoins = balance < COST_PER_MESSAGE;
  const isDisabled = isGenerating || !input.trim() || isOverLimit || insufficientCoins;

  const handleSend = useCallback(() => {
    if (isDisabled) return;
    onSend(input.trim());
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input, isDisabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+Enter 或 Cmd+Enter 发送，普通 Enter 换行
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <div className="px-6 py-4 border-t border-border-subtle flex-shrink-0">
      <div className="bg-bg-surface border border-border-subtle rounded-xl px-4 pt-3 pb-3">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('input.placeholder')}
          rows={1}
          disabled={isGenerating}
          className="w-full bg-transparent text-gray-50 text-sm leading-relaxed
                     resize-none focus:outline-none placeholder:text-gray-500 overflow-y-auto"
        />
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-500">{t('input.newlineHint')}</span>
            <span className="text-[11px] text-gray-500">
              {t('input.cost', { amount: COST_PER_MESSAGE })}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[11px] ${isOverLimit ? 'text-error' : 'text-gray-500'}`}>
              {charCount}/{MAX_LENGTH}
            </span>
            <button
              onClick={handleSend}
              disabled={isDisabled}
              className="w-9 h-9 rounded-[10px] bg-primary hover:bg-primary/90 disabled:bg-bg-elevated disabled:text-gray-500
                         text-white flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
              aria-label={t('input.send')}
            >
              <PaperAirplaneIcon className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
